import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  BookOpen,
  Plus,
  Search,
  Trash2,
  Edit,
  Calendar,
  User,
  Wand2,
  Save,
  X,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  RotateCcw,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface Honor {
  id: string;
  name: string;
  hebrewName: string | null;
  category: string;
  sortOrder: number;
  isActive: boolean;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  hebrewName: string | null;
  name?: string;
  lastHonorDate?: string | null;
}

interface Assignment {
  id: string;
  honorId: string;
  memberId: string;
  serviceDate: string;
  serviceName: string | null;
  parsha: string | null;
  notes: string | null;
  assignedBy: string;
  status: string;
  honor: { id: string; name: string; hebrewName: string | null; category: string };
  member: { id: string; firstName: string; lastName: string; hebrewName: string | null; name: string } | null;
  createdAt: string;
}

// ============================================
// UTILITY
// ============================================

function getNextSaturday(): string {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilSaturday = dayOfWeek === 6 ? 7 : 6 - dayOfWeek;
  const nextSat = new Date(now);
  nextSat.setDate(now.getDate() + daysUntilSaturday);
  return nextSat.toISOString().split('T')[0];
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// ============================================
// MAIN PAGE
// ============================================

export function GabbaiPage() {
  const [activeTab, setActiveTab] = useState<'planner' | 'honors' | 'history'>('planner');

  const tabs = [
    { key: 'planner' as const, label: 'Service Planner', icon: Calendar },
    { key: 'honors' as const, label: 'Honor Types', icon: BookOpen },
    { key: 'history' as const, label: 'History', icon: Clock },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gabbai Tools</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage service honors, aliyot, and assignments
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'planner' && <ServicePlannerTab />}
      {activeTab === 'honors' && <HonorTypesTab />}
      {activeTab === 'history' && <HistoryTab />}
    </div>
  );
}

// ============================================
// TAB 1: SERVICE PLANNER
// ============================================

function ServicePlannerTab() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(getNextSaturday());
  const [parsha, setParsha] = useState('');
  const [serviceName, setServiceName] = useState('Shabbat Morning');
  const [slotAssignments, setSlotAssignments] = useState<Record<string, { memberId: string; memberName: string }>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [activeSearchHonorId, setActiveSearchHonorId] = useState<string | null>(null);

  const { data: honors } = useQuery({
    queryKey: ['gabbai-honors'],
    queryFn: async () => {
      const response = await api.get<Honor[]>('/gabbai/honors');
      if (!response.success) throw new Error(response.error?.message);
      return (response.data || []).filter((h) => h.isActive);
    },
  });

  const { data: dateAssignments } = useQuery({
    queryKey: ['gabbai-date-assignments', selectedDate],
    queryFn: async () => {
      const response = await api.get<Assignment[]>(`/gabbai/assignments/by-date/${selectedDate}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  // Load existing assignments into slot map
  useEffect(() => {
    if (dateAssignments && dateAssignments.length > 0) {
      const map: Record<string, { memberId: string; memberName: string }> = {};
      for (const a of dateAssignments) {
        if (a.member) {
          map[a.honorId] = { memberId: a.member.id, memberName: a.member.name };
        }
        if (a.parsha) setParsha(a.parsha);
        if (a.serviceName) setServiceName(a.serviceName);
      }
      setSlotAssignments(map);
    } else {
      setSlotAssignments({});
    }
  }, [dateAssignments]);

  const { data: searchResults } = useQuery({
    queryKey: ['member-search', searchQueries[activeSearchHonorId || '']],
    queryFn: async () => {
      const q = searchQueries[activeSearchHonorId || ''];
      if (!q || q.length < 2) return [];
      const response = await api.get<Member[]>(`/members?search=${encodeURIComponent(q)}&limit=10&status=active`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!activeSearchHonorId && (searchQueries[activeSearchHonorId || '']?.length || 0) >= 2,
  });

  const { data: suggestions } = useQuery({
    queryKey: ['gabbai-suggestions'],
    queryFn: async () => {
      const response = await api.get<Member[]>('/gabbai/suggest');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Delete existing assignments for this date first
      if (dateAssignments) {
        for (const a of dateAssignments) {
          await api.delete(`/gabbai/assignments/${a.id}`);
        }
      }
      // Create new assignments
      const promises = Object.entries(slotAssignments).map(([honorId, { memberId }]) =>
        api.post('/gabbai/assignments', {
          honorId,
          memberId,
          serviceDate: selectedDate,
          serviceName,
          parsha: parsha || undefined,
        })
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-date-assignments', selectedDate] });
    },
  });

  const handleSuggest = useCallback((honorId: string) => {
    if (!suggestions || suggestions.length === 0) return;
    const assignedMemberIds = new Set(Object.values(slotAssignments).map((s) => s.memberId));
    const available = suggestions.find((s) => !assignedMemberIds.has(s.id));
    if (available) {
      setSlotAssignments((prev) => ({
        ...prev,
        [honorId]: {
          memberId: available.id,
          memberName: available.name || `${available.firstName} ${available.lastName}`,
        },
      }));
    }
  }, [suggestions, slotAssignments]);

  const handleClearAll = () => {
    setSlotAssignments({});
    setParsha('');
  };

  return (
    <div className="space-y-6">
      {/* Date & Service Info */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
            <input
              type="text"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              placeholder="e.g., Shabbat Morning"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parsha</label>
            <input
              type="text"
              value={parsha}
              onChange={(e) => setParsha(e.target.value)}
              placeholder="e.g., Bereishit"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Honor Slots */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">
            Honors for {formatDate(selectedDate)}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear All
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || Object.keys(slotAssignments).length === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" />
              {saveMutation.isPending ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {!honors || honors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No honor types configured. Add some in the Honor Types tab.</p>
            </div>
          ) : (
            honors.map((honor) => {
              const assigned = slotAssignments[honor.id];
              const searchQuery = searchQueries[honor.id] || '';
              const isSearchActive = activeSearchHonorId === honor.id && searchQuery.length >= 2;

              return (
                <div key={honor.id} className="px-6 py-3 flex items-center gap-4">
                  {/* Honor Name */}
                  <div className="w-48 flex-shrink-0">
                    <span className="font-medium text-gray-900">{honor.name}</span>
                    {honor.hebrewName && (
                      <span className="ml-2 text-sm text-gray-500">{honor.hebrewName}</span>
                    )}
                    <span className={cn(
                      'ml-2 inline-flex text-xs px-1.5 py-0.5 rounded',
                      honor.category === 'aliyah' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {honor.category}
                    </span>
                  </div>

                  {/* Member Assignment */}
                  <div className="flex-1 relative">
                    {assigned ? (
                      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        <User className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">{assigned.memberName}</span>
                        <button
                          onClick={() => {
                            setSlotAssignments((prev) => {
                              const next = { ...prev };
                              delete next[honor.id];
                              return next;
                            });
                          }}
                          className="ml-auto text-green-500 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQueries((prev) => ({ ...prev, [honor.id]: e.target.value }));
                            setActiveSearchHonorId(honor.id);
                          }}
                          onFocus={() => setActiveSearchHonorId(honor.id)}
                          placeholder="Search member by name..."
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        {isSearchActive && searchResults && searchResults.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {searchResults.map((m) => (
                              <button
                                key={m.id}
                                onClick={() => {
                                  setSlotAssignments((prev) => ({
                                    ...prev,
                                    [honor.id]: {
                                      memberId: m.id,
                                      memberName: `${m.firstName} ${m.lastName}`,
                                    },
                                  }));
                                  setSearchQueries((prev) => ({ ...prev, [honor.id]: '' }));
                                  setActiveSearchHonorId(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <User className="h-3.5 w-3.5 text-gray-400" />
                                <span>{m.firstName} {m.lastName}</span>
                                {m.hebrewName && (
                                  <span className="text-gray-400 text-xs">({m.hebrewName})</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Suggest Button */}
                  {!assigned && (
                    <button
                      onClick={() => handleSuggest(honor.id)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg border border-primary-200"
                      title="Auto-suggest a member"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      Suggest
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {saveMutation.isSuccess && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          Assignments saved successfully.
        </div>
      )}
    </div>
  );
}

// ============================================
// TAB 2: HONOR TYPES
// ============================================

function HonorTypesTab() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingHonor, setEditingHonor] = useState<Honor | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: honors, isLoading } = useQuery({
    queryKey: ['gabbai-honors'],
    queryFn: async () => {
      const response = await api.get<Honor[]>('/gabbai/honors');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; hebrewName?: string; category: string; sortOrder: number }) => {
      const response = await api.post<Honor>('/gabbai/honors', data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-honors'] });
      setShowModal(false);
      setEditingHonor(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Honor> }) => {
      const response = await api.patch<Honor>(`/gabbai/honors/${id}`, data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-honors'] });
      setShowModal(false);
      setEditingHonor(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/gabbai/honors/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-honors'] });
      setDeleteConfirm(null);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.patch<Honor>(`/gabbai/honors/${id}`, { isActive });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-honors'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingHonor(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add Honor Type
        </button>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hebrew Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sort Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!honors || honors.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No honor types configured
                </td>
              </tr>
            ) : (
              honors.map((honor) => (
                <tr key={honor.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{honor.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{honor.hebrewName || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                      honor.category === 'aliyah' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    )}>
                      {honor.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{honor.sortOrder}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActiveMutation.mutate({ id: honor.id, isActive: !honor.isActive })}
                      className={cn(
                        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                        honor.isActive ? 'bg-primary-600' : 'bg-gray-300'
                      )}
                    >
                      <span className={cn(
                        'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                        honor.isActive ? 'translate-x-4.5' : 'translate-x-1'
                      )} style={{ transform: honor.isActive ? 'translateX(18px)' : 'translateX(3px)' }} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditingHonor(honor);
                          setShowModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(honor.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <HonorModal
          honor={editingHonor}
          onClose={() => {
            setShowModal(false);
            setEditingHonor(null);
          }}
          onSubmit={(data) => {
            if (editingHonor) {
              updateMutation.mutate({ id: editingHonor.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Honor Type</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            {deleteMutation.isError && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {(deleteMutation.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HonorModal({
  honor,
  onClose,
  onSubmit,
  isLoading,
}: {
  honor: Honor | null;
  onClose: () => void;
  onSubmit: (data: { name: string; hebrewName?: string; category: string; sortOrder: number }) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: honor?.name || '',
    hebrewName: honor?.hebrewName || '',
    category: honor?.category || 'aliyah',
    sortOrder: honor?.sortOrder?.toString() || '0',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      hebrewName: formData.hebrewName || undefined,
      category: formData.category,
      sortOrder: parseInt(formData.sortOrder) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {honor ? 'Edit Honor Type' : 'New Honor Type'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Kohen, Levi, Shlishi"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hebrew Name</label>
            <input
              type="text"
              value={formData.hebrewName}
              onChange={(e) => setFormData({ ...formData, hebrewName: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="aliyah">Aliyah</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
            <input
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : honor ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// TAB 3: HISTORY
// ============================================

function HistoryTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const buildParams = () => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: '20',
    });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return params;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['gabbai-assignments', page, startDate, endDate],
    queryFn: async () => {
      const response = await api.get<Assignment[]>(`/gabbai/assignments?${buildParams()}`);
      if (!response.success) throw new Error(response.error?.message);
      return { assignments: response.data || [], meta: response.meta };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/gabbai/assignments/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gabbai-assignments'] });
      setDeleteConfirm(null);
    },
  });

  const filteredAssignments = data?.assignments.filter((a) => {
    if (memberSearch) {
      const name = a.member?.name?.toLowerCase() || '';
      if (!name.includes(memberSearch.toLowerCase())) return false;
    }
    return true;
  }) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'declined': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div className="relative flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-500 mb-1">Member</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Filter by member name..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Honor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parsha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAssignments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No assignments found
                      </td>
                    </tr>
                  ) : (
                    filteredAssignments.map((assignment) => (
                      <tr key={assignment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDate(assignment.serviceDate.split('T')[0])}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{assignment.honor.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {assignment.member?.name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{assignment.parsha || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', getStatusColor(assignment.status))}>
                            {assignment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDeleteConfirm(assignment.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <p className="text-sm text-gray-500">
                  Page {data.meta.page} of {data.meta.totalPages} ({data.meta.total} assignments)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= (data.meta?.totalPages || 1)}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Assignment</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
